import { useState, useEffect } from 'react';
import type { FormType } from '@/types/form';
import { formStorage } from '@/utils/form-storage';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Eye, Download, FileText, Search, Copy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

const FormsManager = () => {
  const navigate = useNavigate();
  const [forms, setForms] = useState<FormType[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<FormType['status'] | 'all'>('all');
  const [filterCategory, setFilterCategory] = useState<FormType['category'] | 'all'>('all');

  useEffect(() => {
    loadForms();
  }, []);

  const loadForms = () => {
    const loadedForms = formStorage.getAllForms();
    setForms(loadedForms);
  };

  const filteredForms = forms.filter(form => {
    const matchesSearch = form.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         form.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || form.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || form.category === filterCategory;
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const handleCreateNew = () => {
    navigate('/forms/new');
  };

  const handleEditForm = (formId: string) => {
    navigate(`/forms/edit/${formId}`);
  };

  const handleDuplicateForm = async (formId: string) => {
    try {
      const duplicated = formStorage.duplicateForm(formId);
      loadForms();
      toast.success(`Form "${duplicated.name}" created successfully`);
    } catch (error) {
      toast.error('Failed to duplicate form');
    }
  };

  const handleDeleteForm = async (formId: string, formName: string) => {
    if (window.confirm(`Are you sure you want to delete "${formName}"? This action cannot be undone.`)) {
      try {
        formStorage.deleteForm(formId);
        loadForms();
        toast.success('Form deleted successfully');
      } catch (error) {
        toast.error('Failed to delete form');
      }
    }
  };

  const handleExportForm = (form: FormType) => {
    const dataStr = JSON.stringify(form, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${form.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Form exported successfully');
  };

  const getStatusColor = (status: FormType['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'archived':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  const getCategoryColor = (category: FormType['category']) => {
    switch (category) {
      case 'report':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'project':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
      case 'survey':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'application':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Forms Manager</h1>
          <p className="text-muted-foreground mt-2">
            Create and manage dynamic form configurations with field types and validation
          </p>
        </div>
        <Button className="flex items-center gap-2" onClick={handleCreateNew}>
          <Plus className="h-4 w-4" />
          Create New Form
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search forms..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as FormType['status'] | 'all')}
                className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>

              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value as FormType['category'] | 'all')}
                className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="all">All Categories</option>
                <option value="report">Report</option>
                <option value="project">Project</option>
                <option value="survey">Survey</option>
                <option value="application">Application</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Forms List */}
      <div className="grid grid-cols-1 gap-6">
        {filteredForms.map((form) => (
          <Card key={form.id} className="hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="space-y-2 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <CardTitle className="text-xl">{form.name}</CardTitle>
                    <Badge className={getStatusColor(form.status)} variant="secondary">
                      {form.status}
                    </Badge>
                    <Badge className={getCategoryColor(form.category)} variant="secondary">
                      {form.category}
                    </Badge>
                  </div>
                  <CardDescription>{form.description}</CardDescription>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                    <span>{form.fields.length} fields</span>
                    <span>•</span>
                    <span>Version {form.version}</span>
                    <span>•</span>
                    <span>Updated {new Date(form.metadata.updatedAt).toLocaleDateString()}</span>
                    {form.metadata.tags.length > 0 && (
                      <>
                        <span>•</span>
                        <div className="flex gap-1">
                          {form.metadata.tags.slice(0, 2).map(tag => (
                            <Badge key={tag} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {form.metadata.tags.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{form.metadata.tags.length - 2}
                            </Badge>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    title="Preview form"
                    onClick={() => handleEditForm(form.id)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    title="Export form"
                    onClick={() => handleExportForm(form)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    title="Duplicate form"
                    onClick={() => handleDuplicateForm(form.id)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    title="Edit form"
                    onClick={() => handleEditForm(form.id)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    title="Delete form"
                    onClick={() => handleDeleteForm(form.id, form.name)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Form Configuration</p>
                  <p className="text-xs text-muted-foreground">
                    Dynamic fields with validation, sorting, and conditional logic
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleEditForm(form.id)}
                >
                  Edit Form Builder
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {filteredForms.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm || filterStatus !== 'all' || filterCategory !== 'all' 
                ? 'No forms match your filters' 
                : 'No forms created yet'
              }
            </h3>
            <p className="text-muted-foreground text-center mb-4">
              {searchTerm || filterStatus !== 'all' || filterCategory !== 'all'
                ? 'Try adjusting your search criteria or filters'
                : 'Create your first dynamic form with custom fields and validation rules'
              }
            </p>
            <Button onClick={handleCreateNew}>
              <Plus className="h-4 w-4 mr-2" />
              Create Form
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      {forms.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold">{forms.length}</div>
                <div className="text-xs text-muted-foreground">Total Forms</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{forms.filter(f => f.status === 'active').length}</div>
                <div className="text-xs text-muted-foreground">Active</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{forms.filter(f => f.status === 'draft').length}</div>
                <div className="text-xs text-muted-foreground">Drafts</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{forms.reduce((sum, f) => sum + f.fields.length, 0)}</div>
                <div className="text-xs text-muted-foreground">Total Fields</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FormsManager;